import * as R from 'ramda';
import { sortByPropCaseInsensitive } from '@trs/utils';

const getNumber = (value) => {
  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) ? parsedValue : 0;
};

export const mapRequestToSharesModel = (shares) =>
  shares.map((share) => {
    const vested = {
      count: R.pathOr(0, ['vested', 'count'], share),
      percentage: R.pathOr(0, ['vested', 'percentage'], share),
      amount: R.pathOr({}, ['vested', 'amount'], share),
      employeeAmount: R.pathOr({}, ['vested', 'employeeAmount'], share),
    };

    const unvested = {
      count: R.pathOr(0, ['unvested', 'count'], share),
      percentage: R.pathOr(0, ['unvested', 'percentage'], share),
      amount: R.pathOr({}, ['unvested', 'amount'], share),
      employeeAmount: R.pathOr({}, ['unvested', 'employeeAmount'], share),
    };

    const data = {
      name: share.name,
      provider: share.provider,
      sharePrice: share.sharePrice,
      priceLastUpdated: share.priceLastUpdated,
      vested,
      unvested,
      unvestedCount: unvested.count,
      shareData: [
        {
          shareStatus: 'vested',
          count: vested.count,
          /** The value key is used by the recharts library in order to display the pie chart */
          value: vested.percentage,
          amount: vested.amount,
          employeeAmount: vested.employeeAmount,
        },
        {
          shareStatus: 'unvested',
          count: unvested.count,
          value: unvested.percentage,
          amount: unvested.amount,
          employeeAmount: unvested.employeeAmount,
        },
      ],
    };

    return data;
  });

export const getSplitId = (input = '') => R.head(R.split('_', input));

export const getCategoryFromCategoryId = (categoryLevel1Id) =>
  R.find(R.propEq('id', getSplitId(categoryLevel1Id)));

export const getNameFromCategoryId = R.curry((categoryMappings, category) => {
  const categoryObj = getCategoryFromCategoryId(R.prop('categoryLevel1Id', category))(
    categoryMappings
  );
  return {
    ...category,
    orderIndex: R.prop('orderIndex', categoryObj),
    displayName: R.prop('displayName', categoryObj),
  };
});

export const mapCategoryNameToId = ({ categoryMappings, employeeCategories }) => ({
  ...employeeCategories,
  categories: R.sortBy(
    R.prop('orderIndex'),
    R.map(getNameFromCategoryId(categoryMappings), R.prop('categories', employeeCategories))
  ),
  extraCategories: R.sortBy(
    R.prop('orderIndex'),
    R.map(getNameFromCategoryId(categoryMappings), R.prop('extraCategories', employeeCategories))
  ),
});

const getIsAmount = (item) => item.isAmount;

export const getSortedTextOnlyRewards = R.compose(
  sortByPropCaseInsensitive('name'),
  R.reject(getIsAmount)
);

export const sortTextOnlyAlphabetically = (data) => {
  // filter only amount data
  const amountData = data.filter(getIsAmount);
  // filter only text and sort items
  const sortedTextOnly = getSortedTextOnlyRewards(data);
  return [...amountData, ...sortedTextOnly];
};

export const getShareVestedCount = (share = {}) => getNumber(R.path(['vested', 'count'], share));

export const getShareUnvestedCount = (share = {}) =>
  getNumber(R.propOr(R.path(['unvested', 'count'], share), 'unvestedCount', share));

export const getShareVestedPercentage = (share = {}) =>
  getNumber(R.path(['vested', 'percentage'], share));

export const getShareUnvestedPercentage = (share = {}) =>
  getNumber(R.path(['unvested', 'percentage'], share));

export const getShareVestedAmount = (share = {}) =>
  getNumber(R.path(['vested', 'amount', 'value'], share));

export const getShareUnvestedAmount = (share = {}) =>
  getNumber(R.path(['unvested', 'amount', 'value'], share));

export const getSharePlanTotalAmount = (share = {}) =>
  getShareVestedAmount(share) + getShareUnvestedAmount(share);

export const getSharePriceValue = (sharePrice) => {
  if (sharePrice && typeof sharePrice === 'object') {
    return R.prop('value', sharePrice);
  }

  return sharePrice;
};

export const getSharePriceCurrency = (sharePrice, employeeCurrency) => {
  if (sharePrice && typeof sharePrice === 'object') {
    return R.propOr(employeeCurrency, 'currency', sharePrice);
  }

  return employeeCurrency;
};

export const getSharesSummaryFromModel = (shares = []) => {
  const safeShares = Array.isArray(shares) ? shares : [];

  const totals = safeShares.reduce(
    (accumulator, share) => {
      const vestedCount = getShareVestedCount(share);
      const unvestedCount = getShareUnvestedCount(share);
      const vestedAmount = getShareVestedAmount(share);
      const unvestedAmount = getShareUnvestedAmount(share);

      return {
        totalShares: accumulator.totalShares + vestedCount + unvestedCount,
        totalVestedShares: accumulator.totalVestedShares + vestedCount,
        totalUnvestedShares: accumulator.totalUnvestedShares + unvestedCount,
        totalVestedAmount: accumulator.totalVestedAmount + vestedAmount,
        totalUnvestedAmount: accumulator.totalUnvestedAmount + unvestedAmount,
      };
    },
    {
      totalShares: 0,
      totalVestedShares: 0,
      totalUnvestedShares: 0,
      totalVestedAmount: 0,
      totalUnvestedAmount: 0,
    }
  );

  const totalValueAmount = totals.totalVestedAmount + totals.totalUnvestedAmount;

  return {
    ...totals,
    totalValueAmount,
    overallVestedPercentage:
      totalValueAmount > 0 ? Math.round((totals.totalVestedAmount / totalValueAmount) * 100) : 0,
    overallUnvestedPercentage:
      totalValueAmount > 0 ? Math.round((totals.totalUnvestedAmount / totalValueAmount) * 100) : 0,
  };
};

export default {
  mapRequestToSharesModel,
  mapCategoryNameToId,
  sortTextOnlyAlphabetically,
  getSortedTextOnlyRewards,
  getShareVestedCount,
  getShareUnvestedCount,
  getShareVestedPercentage,
  getShareUnvestedPercentage,
  getShareVestedAmount,
  getShareUnvestedAmount,
  getSharePlanTotalAmount,
  getSharePriceValue,
  getSharePriceCurrency,
  getSharesSummaryFromModel,
};
